<template>
  <div data-component-group="filter-forms" data-component="contract">
    <FiltersForm
      has-phone-sheet
      :applied-filters="appliedFilters"
      :on-clear-filter="clearFilter"
      :has-filters="hasFilters"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="countries"
    >
      <template v-slot:phone-actions>
        <slot name="phone-actions" />
      </template>

      <v-autocomplete
        clearable
        v-model="business"
        :items="businesses"
        :loading="isLoadingBusinesses"
        prepend-inner-icon="domain"
        :return-object="true"
        item-value="id"
        item-title="name"
        label="Business"
        v-model:search="businessSearch"
      />

      <v-autocomplete
        clearable
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        :return-object="true"
        item-title="name"
        label="Status"
      />

      <base-date-picker
        :date="date"
        placeholder="Dates"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="owner"
        :items="owners"
        :loading="isLoadingManagers"
        prepend-inner-icon="person"
        label="Contract owner"
        item-title="name"
        :return-object="true"
        v-model:search="ownerSearch"
      />

      <v-autocomplete
        clearable
        v-model="domain"
        :items="domains"
        :loading="isLoadingDomains"
        prepend-inner-icon="account_circle"
        label="Domain"
        item-title="url"
        item-value="url"
        v-model:search="domainSearch"
      />

      <v-autocomplete
        clearable
        v-model="contractType"
        :items="contractTypes"
        prepend-inner-icon="label_important"
        :label="$t('contract_type')"
        item-title="name"
        return-object
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import FiltersForm from './FiltersBase';
  import filterUrlSync from '@/mixins/filterUrlSync';
  import filterChips, { platformChips } from '@/mixins/filterChips';
  import { isNonEmptyArr, isNonEmptyStr, isNum, isStr, debounce } from '../../../utils';

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const ownerSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const domainSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    mixins: [filterUrlSync, filterChips],
    components: {
      FiltersForm,
    },
    emits: ['clearFilters', 'filter'],
    data() {
      return {
        urlConfig: [
          { field: 'countries', urlKey: 'countries', type: 'string[]' },
          { field: 'date', urlKey: 'expire_before', type: 'string' },
          // The domain select stores the raw url (item-value="url"), and the
          // title is that same url, so the stub renders on its own.
          { field: 'domain', urlKey: 'domain', type: 'string' },
          {
            field: 'status', urlKey: 'status', type: 'number', prop: 'id',
            fetchMethod: 'getStatuses', itemsField: 'statuses',
          },
          {
            field: 'contractType', urlKey: 'type', type: 'number', prop: 'id',
            fetchMethod: 'getContractTypes', itemsField: 'contractTypes',
          },
          {
            // Both looked up by id: the value the URL names can sit outside the
            // page the search fetch returns.
            field: 'business', urlKey: 'business', type: 'number', prop: 'id',
            fetchByIds: 'getBusinessesByIds',
          },
          {
            field: 'owner', urlKey: 'owner', type: 'number', prop: 'id',
            fetchByIds: 'getManagersByIds',
          },
        ],
        business: null,
        businessSearch: '',
        contractType: null,
        countries: [],
        domain: null,
        domains: [],
        status: null,
        owner: null,
        owners: [],
        ownerSearch: '',
        domainSearch: '',
        date: null,
        isLoadingBusinesses: false,
        isLoadingManagers: false,
        isLoadingDomains: false,
        isRefreshingBusinesses: false,
        isRefreshingOwner: false,
      };
    },
    computed: {
      filterChipFields() {
        const platforms = this.$store.getters['users/countries'] || [];

        return [
          {
            model: 'countries',
            label: this.$t('platform'),
            toChips: platformChips(platforms),
          },
          { model: 'business', label: 'Business' },
          { model: 'status', label: 'Status' },
          { model: 'date', label: 'Dates' },
          { model: 'owner', label: 'Contract owner' },
          {
            model: 'domain',
            label: 'Domain',
            clear: () => {
              this.domain = null;
              this.domainSearch = '';
            },
          },
          { model: 'contractType', label: this.$t('contract_type') },
        ];
      },
      hasFilters() {
        const {
          contractType = {},
          business = {},
          countries = [],
          status = {},
          owner = {},
          domain = '',
          date,
        } = this;

        return (
          isNum(contractType?.id) ||
          isNum(business?.id) ||
          isNonEmptyArr(countries) ||
          isNum(status?.id) ||
          isNum(owner?.id) ||
          isNonEmptyStr(domain) ||
          !!date
        );
      },
      contractTypes() {
        return this.$store.getters['contracts/contractTypes'];
      },
      businesses() {
        const items = this.$store.getters['businesses/items'];
        // A business restored from the URL is looked up by id, so it is not in
        // the listing the store holds. Keep it among the options.
        return this.business && !items.some(({ id }) => id === this.business.id)
          ? [...items, this.business]
          : items;
      },
      statuses() {
        return this.$store.getters['contracts/statuses'];
      },
    },
    watch: {
      business() {
        this.isRefreshingBusinesses = true;
      },
      businessSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBusinesses) {
            this.isRefreshingBusinesses = false;
            return false;
          }

          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      owner() {
        this.isRefreshingOwner = true;
      },
      ownerSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingOwner) {
            this.isRefreshingOwner = false;
            return false;
          }

          ownerSearchDebouncer(() => this.getManagers(nextSearch));
        }
      },
      domainSearch(nextSearch) {
        if (isStr(nextSearch) && nextSearch.length > 2) {
          if (this.isLoadingDomains) {
            this.isLoadingDomains = false;
            return false;
          }

          domainSearchDebouncer(() => this.getDomains(nextSearch));
        }
      },
    },
    created() {
      const { statuses } = this;

      if (statuses.length === 0) {
        this.getStatuses();
      }
    },
    methods: {
      onDomainFocus() {
        if (this.contractTypes.length === 0) {
          this.getDomains();
        }
      },
      getDomains(search = '') {
        this.isLoadingDomains = true;
        return this.$store
          .dispatch('contracts/getDomains', {search})
          .then((res) => {
            this.domains = res;
          })
          .finally(() => {
            this.isLoadingDomains = false;
          });
      },
      getStatuses() {
        return this.$store.dispatch('contracts/getStatuses');
      },
      getContractTypes() {
        return this.$store.dispatch('contracts/getContractTypes');
      },
      onFilter() {
        const {
          contractType = {},
          business = {},
          countries = [],
          status = {},
          owner = {},
          domain = '',
          date,
        } = this;

        this.syncFiltersToUrl();
        this.rememberAppliedFilters();

        this.$emit('filter', {
          type: contractType?.id,
          business: business?.id,
          countries,
          status: status?.id,
          owner: owner?.id,
          domain,
          expire_before: date,
        });
      },
      onClearFilters() {
        this.countries = [];
        this.domainSearch = '';
        this.date = null;

        this.domain = null;
        this.owner = null;
        this.status = null;
        this.contractType = null;
        this.business = null;

        this.clearFiltersUrl();
        this.forgetAppliedFilters();

        this.$emit('clearFilters');
      },
      onChangeDate(date) {
        this.date = date;
      },
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItems', query)
          .then((res) => {
            this.isLoadingBusinesses = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      getManagersByIds(ids) {
        this.isLoadingManagers = true;
        return this.$store
          .dispatch('users/getManagers', { ids, per_page: ids.length })
          .then((items) => {
            // `owners` is local data here, so the looked-up row has to be put
            // into it or the select has a value it cannot offer.
            this.owners = [...this.owners, ...items];
            return items;
          })
          .finally(() => {
            this.isLoadingManagers = false;
          });
      },
      getBusinessesByIds(ids) {
        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItemsByIds', { ids })
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      getManagers(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingManagers = true;
        return this.$store
          .dispatch('users/getManagers', query)
          .then((items) => {
            this.owners = items;
            this.isLoadingManagers = false;
            return items;
          })
          .catch((err) => {
            this.isLoadingManagers = false;
            return err;
          });
      },
    },
  };
</script>
